import { useState, useEffect, useCallback, useRef, type MouseEvent, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Users, TrendingUp, Anchor, Plus, CheckCircle, XCircle,
  Pencil, Archive, ArchiveRestore, Trash2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { Modal } from '../components/Modal';

interface MarinaStat {
  id: number;
  name: string;
  slug: string;
  is_active: boolean;
  logo_url: string | null;
  stats: {
    total_berths: number;
    checked_in: number;
    occupancy_pct: number;
    monthly_revenue: number;
    currency: string;
  };
}

interface OrgOverview {
  total_bookings: number;
  total_revenue: number;
  currency: string;
  active_guests: number;
  total_chats: number;
}

interface Org {
  id: number;
  name: string;
  max_marinas: number;
  subscription_tier: string;
  subscription_status: string;
}

export function OrgOverviewPage() {
  const { t } = useTranslation();
  const { setActiveMarina, activeMarinaId, isOrgAdmin } = useAuth();
  const { error: toastError, success: toastSuccess } = useToast();
  const navigate = useNavigate();

  const [org, setOrg] = useState<Org | null>(null);
  const [marinas, setMarinas] = useState<MarinaStat[]>([]);
  const [overview, setOverview] = useState<OrgOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAddMarina, setShowAddMarina] = useState(false);
  const [editMarina, setEditMarina] = useState<MarinaStat | null>(null);
  const [deleteMarina, setDeleteMarina] = useState<MarinaStat | null>(null);
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const [orgData, marinasData, overviewData] = await Promise.all([
        api.getOrg(),
        api.getOrgMarinas(),
        api.getOrgOverview(),
      ]);
      setOrg(orgData as unknown as Org);
      setMarinas((marinasData.marinas ?? []) as unknown as MarinaStat[]);
      setOverview(overviewData as unknown as OrgOverview);
    } catch (err) {
      if (err instanceof ApiError) toastError(err.message);
    } finally {
      setLoading(false);
    }
  }, [toastError]);

  useEffect(() => { load(); }, [load]);

  // Refetch when tab regains visibility (e.g. returning from Stripe portal)
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [load]);

  // Background reconcile: sync tier/status from Stripe for org_admins.
  // Runs once after initial data loads so the subtitle tier stays accurate.
  const reconciledRef = useRef(false);
  useEffect(() => {
    if (!isOrgAdmin || reconciledRef.current || !org) return;
    reconciledRef.current = true;

    api.reconcileBilling()
      .then((data) => {
        const billing = data as { tier?: string; status?: string };
        if (!billing.tier) return;
        setOrg((prev) =>
          prev
            ? { ...prev, subscription_tier: billing.tier as string, subscription_status: (billing.status as string) ?? prev.subscription_status }
            : prev,
        );
      })
      .catch(() => { /* non-critical — org may not have Stripe account yet */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOrgAdmin, org?.id]);

  const handleSelectMarina = (marina: MarinaStat) => {
    setActiveMarina(marina.id);
    navigate('/');
  };

  const handleToggleArchive = async (marina: MarinaStat, e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (actionLoading !== null) return;
    const nextActive = !marina.is_active;
    if (!nextActive && marinas.filter((m) => m.is_active).length <= 1) {
      toastError(t('orgOverview.cannotDeactivateLast'));
      return;
    }
    setActionLoading(marina.id);
    try {
      await api.setMarinaActive(marina.id, nextActive);
      if (activeMarinaId === marina.id && marina.is_active) {
        const fallback = marinas.find((m) => m.id !== marina.id && m.is_active);
        setActiveMarina(fallback?.id ?? null);
      }
      toastSuccess(
        nextActive ? t('orgOverview.marinaActivated') : t('orgOverview.marinaArchived'),
      );
      await load();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'DOMAIN_WHITELIST_REQUIRED') {
        toastError(t('orgOverview.domainWhitelistRequired'));
      } else {
        toastError(err instanceof ApiError ? err.message : t('errors.somethingWentWrong'));
      }
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
      </div>
    );
  }

  const currency = overview?.currency ?? 'EUR';

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">{org?.name}</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {t('orgOverview.managingMarinas', { count: marinas.length })} ·{' '}
            <span className="capitalize">{org?.subscription_tier}</span>
          </p>
        </div>
        {org && marinas.length < org.max_marinas && (
          <button
            onClick={() => setShowAddMarina(true)}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 transition-colors"
          >
            <Plus className="w-4 h-4" /> {t('orgOverview.addMarina')}
          </button>
        )}
      </div>

      {overview && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: t('orgOverview.bookingsThisMonth'), value: overview.total_bookings, icon: Anchor },
            { label: t('orgOverview.revenueThisMonth'), value: `${overview.total_revenue.toFixed(0)} ${currency}`, icon: TrendingUp },
            { label: t('orgOverview.activeGuests'), value: overview.active_guests, icon: Users },
            { label: t('orgOverview.aiChatsThisMonth'), value: overview.total_chats, icon: Building2 },
          ].map(({ label, value, icon: Icon }) => (
            <div key={label} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-2 text-gray-400 mb-1">
                <Icon className="w-4 h-4" />
                <span className="text-xs">{label}</span>
              </div>
              <p className="text-xl font-bold text-gray-900">{value}</p>
            </div>
          ))}
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {marinas.map((marina) => (
          <div
            key={marina.id}
            className="relative bg-white rounded-xl border border-gray-200 hover:border-brand-400 hover:shadow-sm transition-all group"
          >
            <div
              className="absolute top-3 right-3 z-10 flex items-center gap-0.5"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
            >
              <button
                type="button"
                title={t('orgOverview.editMarina')}
                onClick={() => setEditMarina(marina)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-brand-600 hover:bg-brand-50 transition-colors"
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                type="button"
                title={marina.is_active ? t('orgOverview.archiveMarina') : t('orgOverview.restoreMarina')}
                disabled={actionLoading === marina.id}
                onClick={(e) => void handleToggleArchive(marina, e)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-amber-700 hover:bg-amber-50 transition-colors disabled:opacity-50"
              >
                {marina.is_active ? <Archive className="w-4 h-4" /> : <ArchiveRestore className="w-4 h-4" />}
              </button>
              {!marina.is_active && marinas.length > 1 && (
                <button
                  type="button"
                  title={t('orgOverview.deleteMarinaPermanently')}
                  disabled={actionLoading === marina.id}
                  onClick={() => setDeleteMarina(marina)}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={() => handleSelectMarina(marina)}
              className="w-full p-5 text-left rounded-xl"
            >
              <div className="flex items-start justify-between mb-3 pr-20">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-brand-50 flex items-center justify-center shrink-0">
                    <Anchor className="w-4 h-4 text-brand-600" />
                  </div>
                  <span className="font-semibold text-gray-900 text-sm group-hover:text-brand-700 truncate">{marina.name}</span>
                </div>
                {marina.is_active ? (
                  <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded-full shrink-0">
                    <CheckCircle className="w-3 h-3" /> {t('orgOverview.active')}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full shrink-0">
                    <XCircle className="w-3 h-3" /> {t('orgOverview.inactive')}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <p className="text-gray-400">{t('orgOverview.occupancy')}</p>
                  <p className="font-semibold text-gray-700">{marina.stats.occupancy_pct}%</p>
                </div>
                <div>
                  <p className="text-gray-400">{t('orgOverview.checkedIn')}</p>
                  <p className="font-semibold text-gray-700">{marina.stats.checked_in} / {marina.stats.total_berths}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-gray-400">{t('orgOverview.revenueThisMonth')}</p>
                  <p className="font-semibold text-gray-700">{marina.stats.monthly_revenue.toFixed(0)} {marina.stats.currency}</p>
                </div>
              </div>
            </button>
          </div>
        ))}
      </div>

      <AddMarinaModal
        open={showAddMarina}
        onClose={() => setShowAddMarina(false)}
        onCreated={() => { setShowAddMarina(false); load(); }}
      />
      <EditMarinaModal
        open={editMarina !== null}
        marina={editMarina}
        onClose={() => setEditMarina(null)}
        onSaved={() => { setEditMarina(null); load(); }}
      />
      <DeleteMarinaModal
        open={deleteMarina !== null}
        marina={deleteMarina}
        onClose={() => setDeleteMarina(null)}
        onDeleted={() => { setDeleteMarina(null); load(); }}
        activeMarinaId={activeMarinaId}
        setActiveMarina={setActiveMarina}
      />
    </div>
  );
}

// ── Add Marina Modal ──────────────────────────────────────────

function AddMarinaModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const { t } = useTranslation();
  const { error: toastError, success: toastSuccess } = useToast();
  const [form, setForm] = useState({ name: '', slug: '', timezone: 'Europe/Tallinn', contact_email: '', contact_phone: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setForm({ name: '', slug: '', timezone: 'Europe/Tallinn', contact_email: '', contact_phone: '' });
  }, [open]);

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const handleNameChange = (name: string) => {
    const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    setForm(f => ({ ...f, name, slug }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.createOrgMarina(form);
      toastSuccess(t('orgOverview.marinaCreated', { name: form.name }));
      onCreated();
    } catch (err) {
      if (err instanceof ApiError) {
        const raw = err.raw as { details?: string; hint?: string } | undefined;
        const extra = [raw?.details, raw?.hint].filter(Boolean).join(' — ');
        toastError(extra ? `${err.message} — ${extra}` : err.message);
      } else {
        toastError(t('orgOverview.failedToCreate'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/40';

  return (
    <Modal open={open} onClose={onClose} title={t('orgOverview.addMarinaTitle')} maxWidth="max-w-md">
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('orgOverview.marinaName')}</label>
          <input type="text" value={form.name} onChange={e => handleNameChange(e.target.value)} required className={inputClass} autoFocus />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('orgOverview.urlSlug')}</label>
          <input type="text" value={form.slug} onChange={e => set('slug', e.target.value)} required className={inputClass} />
          <p className="text-xs text-gray-400 mt-1">{t('orgOverview.urlSlugHint')}</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('orgOverview.contactEmail')}</label>
            <input type="email" value={form.contact_email} onChange={e => set('contact_email', e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">{t('orgOverview.contactPhone')}</label>
            <input type="text" value={form.contact_phone} onChange={e => set('contact_phone', e.target.value)} className={inputClass} />
          </div>
        </div>
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">{t('common.cancel')}</button>
          <button type="submit" disabled={submitting} className="flex-1 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 disabled:opacity-50">
            {submitting ? t('orgOverview.creating') : t('orgOverview.createMarina')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Edit Marina Modal ─────────────────────────────────────────

function EditMarinaModal({
  open,
  marina,
  onClose,
  onSaved,
}: {
  open: boolean;
  marina: MarinaStat | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const { error: toastError, success: toastSuccess } = useToast();
  const [form, setForm] = useState({ name: '', slug: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && marina) {
      setForm({ name: marina.name, slug: marina.slug });
    }
  }, [open, marina]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!marina || submitting) return;
    setSubmitting(true);
    try {
      await api.updateOrgMarina(marina.id, {
        name: form.name.trim(),
        slug: form.slug.trim(),
      });
      toastSuccess(t('orgOverview.marinaUpdated'));
      onSaved();
    } catch (err) {
      toastError(err instanceof ApiError ? err.message : t('errors.failedToSave'));
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/40';

  if (!marina) return null;

  return (
    <Modal open={open} onClose={onClose} title={t('orgOverview.editMarinaTitle')} maxWidth="max-w-md">
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('orgOverview.marinaName')}</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            required
            className={inputClass}
            autoFocus
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('orgOverview.urlSlug')}</label>
          <input
            type="text"
            value={form.slug}
            onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
            required
            className={inputClass}
          />
          <p className="text-xs text-gray-400 mt-1">{t('orgOverview.urlSlugHint')}</p>
        </div>
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="flex-1 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 disabled:opacity-50"
          >
            {submitting ? t('orgOverview.saving') : t('orgOverview.saveMarina')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Delete Marina Modal ───────────────────────────────────────

function DeleteMarinaModal({
  open,
  marina,
  onClose,
  onDeleted,
  activeMarinaId,
  setActiveMarina,
}: {
  open: boolean;
  marina: MarinaStat | null;
  onClose: () => void;
  onDeleted: () => void;
  activeMarinaId: number | null;
  setActiveMarina: (id: number | null) => void;
}) {
  const { t } = useTranslation();
  const { error: toastError, success: toastSuccess } = useToast();
  const [submitting, setSubmitting] = useState(false);

  const handleDelete = async () => {
    if (!marina || submitting) return;
    setSubmitting(true);
    try {
      await api.deleteOrgMarina(marina.id);
      if (activeMarinaId === marina.id) {
        setActiveMarina(null);
      }
      toastSuccess(t('orgOverview.marinaDeleted'));
      onDeleted();
    } catch (err) {
      toastError(err instanceof ApiError ? err.message : t('errors.failedToDelete'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!marina) return null;

  return (
    <Modal open={open} onClose={onClose} title={t('orgOverview.deleteMarinaTitle')} maxWidth="max-w-md">
      <div className="space-y-4">
        <p className="text-sm text-gray-600">{t('orgOverview.deleteMarinaBody', { name: marina.name })}</p>
        <p className="text-xs text-red-600/90">{t('orgOverview.deleteMarinaWarning')}</p>
        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={() => void handleDelete()}
            className="flex-1 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
          >
            {submitting ? t('orgOverview.deleting') : t('orgOverview.deleteMarinaConfirm')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
